import { useEffect, useRef, useState } from "react";
import { http } from "../lib/api";
import { focusAfter } from "../lib/focusNext";
import { Search } from "lucide-react";

// SGK meslek kodu seçici: kod kutusuna kod/ünvan yazılır, listeden seçilince ünvan kutusu kapalı (readonly) dolar
export default function OccupationPicker({ code, title, onChange, cls }) {
  const [q, setQ] = useState(code || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const box = useRef(null);
  const listRef = useRef(null);
  useEffect(() => { setQ(code || ""); }, [code]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => http.get("/sgk-occupations", { params: { q, limit: 40 } }).then((r) => { setOpts(r.data); setHi(0); }).catch(() => setOpts([])), 150);
    return () => clearTimeout(t);
  }, [q, open]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => {
    const el = listRef.current?.children?.[hi];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [hi, opts]);
  const pick = (o) => { onChange({ code: o.code, title: o.title }); setQ(o.code); setOpen(false); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); if (!open) setOpen(true); else setHi((h) => Math.min(h + 1, opts.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      // yazılmış aramada vurgulu satırı seç; arama boşsa değeri koru — her iki durumda listeyi kapat ve yan kutuya geç
      e.preventDefault();
      if (open && q.trim() && opts[hi]) pick(opts[hi]); else setOpen(false);
      setTimeout(() => focusAfter(e.target), 0);
    }
    else if (e.key === "Escape") { setOpen(false); }
  };
  return (
    <div className="grid grid-cols-[130px_1fr] gap-2" ref={box}>
      <div className="relative">
        <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => { const v = e.target.value.replace(/,/g, "."); setQ(v); setOpen(true); if (!v) onChange({ code: "", title: "" }); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
          placeholder="Kod / ara…" data-testid="pf-occupation-code" className={`${cls} pl-6 font-mono`} autoComplete="off" role="combobox" aria-expanded={open} />
        {open && (
          <div ref={listRef} className="absolute z-30 left-0 mt-1 w-[420px] max-h-64 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-lg text-xs" data-testid="pf-occupation-list" role="listbox">
            {opts.length === 0 && <div className="px-3 py-2 text-slate-400">Sonuç yok</div>}
            {opts.map((o, i) => (
              <button type="button" key={o.code} onClick={() => pick(o)} onMouseEnter={() => setHi(i)} data-testid={`pf-occupation-opt-${o.code}`} role="option" aria-selected={i === hi}
                className={`w-full text-left px-3 py-1.5 flex gap-2 border-b border-slate-100 last:border-0 ${i === hi ? "bg-orange-100" : "hover:bg-orange-50"}`}>
                <span className="font-mono font-semibold text-slate-700 w-16 shrink-0">{o.code}</span><span>{o.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <input value={title || ""} readOnly tabIndex={-1} placeholder="Meslek ünvanı (koddan gelir)" data-testid="pf-title" className={`${cls} bg-slate-100 text-slate-700 cursor-default`} />
    </div>
  );
}
